// ============================================================================
// JARVISTRAVEL - CODE INPUT
// The 6-box one-digit-per-cell verification-code entry used by email flows
// (verify email at sign-up, change email in Settings). Controlled: the caller
// owns the digits; typing/paste advance focus and fire onComplete when full.
//
// A phone offers a code from Mail or Messages only to a field marked
// one-time-code, and fills the whole code into it in one event. So the first
// box carries that hint and has room for the whole code, and a code that
// arrives in one event is spread across the boxes the way a paste is (JAR-1479).
// ============================================================================

import React, { forwardRef, useImperativeHandle, useRef } from 'react';

export interface CodeInputHandle {
  /** Focus the first box — e.g. after a resend clears the code. */
  focus: () => void;
}

interface CodeInputProps {
  /** One entry per box; length decides how many boxes render. */
  value: string[];
  onChange: (next: string[]) => void;
  /** Fired with the joined code the moment every box is filled. */
  onComplete?: (code: string) => void;
  error?: boolean;
}

export const CodeInput = forwardRef<CodeInputHandle, CodeInputProps>(function CodeInput(
  { value, onChange, onComplete, error = false },
  ref,
) {
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  useImperativeHandle(ref, () => ({ focus: () => inputs.current[0]?.focus() }));

  // A whole code at once, from a paste or from autofill, fills from the first box.
  const spread = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, value.length);
    if (!digits) return;
    const next = Array<string>(value.length).fill('');
    for (let i = 0; i < digits.length; i++) next[i] = digits[i];
    onChange(next);
    inputs.current[Math.min(digits.length, value.length - 1)]?.focus();
    if (digits.length === value.length) onComplete?.(next.join(''));
  };

  const handleChange = (index: number, raw: string) => {
    if (!/^\d*$/.test(raw)) return;
    // A whole code in one event is autofill, not a keystroke. Its last digits
    // are the code, in case it landed after a digit already in the box.
    if (raw.length >= value.length) { spread(raw.slice(-value.length)); return; }
    const next = [...value];
    next[index] = raw.slice(-1);
    onChange(next);
    if (raw && index < value.length - 1) inputs.current[index + 1]?.focus();
    if (next.every((d) => d !== '')) onComplete?.(next.join(''));
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    spread(e.clipboardData.getData('text'));
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !value[index] && index > 0) inputs.current[index - 1]?.focus();
  };

  return (
    <div className="flex justify-center gap-2" onPaste={handlePaste}>
      {value.map((digit, index) => (
        <input
          key={index}
          ref={(el) => { inputs.current[index] = el; }}
          type="text"
          inputMode="numeric"
          // Only the first box is offered the code, and it has room for all of it.
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={index === 0 ? value.length : 1}
          value={digit}
          aria-label={`Digit ${index + 1}`}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          className={`w-11 h-12 text-center text-xl font-bold bg-surface-raised text-ink border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent/40 ${error ? 'border-danger' : 'border-border'}`}
        />
      ))}
    </div>
  );
});
